/* Dashboard Builder — single data provider.
 *
 * Fetches every endpoint the tiles can draw from ONCE and shares the result
 * via context, so a dashboard with a dozen tiles makes one burst of calls
 * instead of one-per-tile. Also resolves the active colour palette + money
 * formatters so every tile renders on-brand. */
import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { api } from '@/lib/api';
import { useRegional, formatCurrency } from '@/lib/regional';
import { BandColors, DEFAULT_COLORS, DashFilters } from './types';

export interface Kpis {
  site_count: number; total_cash_exposure: number; avg_cash_exposure: number;
  avg_risk_score: number; high_critical_count: number; high_critical_pct: number;
}
export interface BandCount { band: string; count: number }
export interface MapPoint { lat: number; lng: number; band: string; score: number; site_name: string; site_id?: string }
export interface TopSite { site_id: string; site_name: string; town: string; abm_type: string; band: string; score: number; cash_exposure: number }
export interface ScatterPoint { x: number; y: number; band: string; site_name: string }
export interface AbmRow { abm_type: string; sites: number; Low: number; Medium: number; High: number; Critical: number; total_cash: number }
export interface WorstCat { category: string; flagged_count: number }
export interface SecurityFocus { totals: { yes: number; no: number; na: number }; pass_rate_pct: number; per_question: Array<{ question_code: string; yes: number; no: number; na: number }> }
export interface Insights {
  kpis: Kpis; risk_distribution: BandCount[]; map_points: MapPoint[];
  top_sites: TopSite[]; scatter: ScatterPoint[]; abm_types: AbmRow[];
  worst_categories: WorstCat[]; security_focus: SecurityFocus;
}
export interface OpsKpis { brokers: number; staff_approved: number; staff_registered: number; access_24h: number; access_7d: number; clients: number; policies: number; sites: number }
interface BandBreakdown { low: number; medium: number; high: number; critical: number; total: number }
export interface BrokerRow extends BandBreakdown { broker_id: string; brokerage_name: string }
export interface Distribution { by_band: BandBreakdown; by_broker: BrokerRow[]; by_template: Array<BandBreakdown & { template_id: string; template_name: string }> }
export interface Outstanding {
  overdue_instructions: { count: number; threshold_days: number; preview: Array<{ id: string; client_name: string; site_reference: string; days_overdue: number }> };
  unrated_results: { count: number; preview: unknown[] };
  high_risk_assessments: { count: number; preview: Array<{ assessment_id: string; client_name: string; site_reference: string; band: string; score: number }> };
}

export interface DashData {
  insights: Insights | null;
  ops: OpsKpis | null;
  distribution: Distribution | null;
  outstanding: Outstanding | null;
  loading: boolean;
  colors: BandColors;
  filters: DashFilters;
  fmtMoney: (n: number) => string;
  sym: string;
}

const Ctx = createContext<DashData | undefined>(undefined);

export const DashboardDataProvider: React.FC<{ colors?: Partial<BandColors>; filters?: DashFilters; children: ReactNode }>
  = ({ colors, filters, children }) => {
  const { regional } = useRegional();
  const [insights, setInsights] = useState<Insights | null>(null);
  const [ops, setOps] = useState<OpsKpis | null>(null);
  const [distribution, setDistribution] = useState<Distribution | null>(null);
  const [outstanding, setOutstanding] = useState<Outstanding | null>(null);
  const [loading, setLoading] = useState(true);

  // Static datasets that do NOT depend on the analytics filters — fetched
  // once on mount (previously refetched on every filter change).
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get<OpsKpis>('/dashboard/insurer-kpis'),
      api.get<Distribution>('/risk/dashboard/distribution'),
      api.get<Outstanding>('/risk/dashboard/outstanding'),
    ]).then((res) => {
      if (!alive) return;
      if (res[0].status === 'fulfilled') setOps(res[0].value.data);
      if (res[1].status === 'fulfilled') setDistribution(res[1].value.data);
      if (res[2].status === 'fulfilled') setOutstanding(res[2].value.data);
    });
    return () => { alive = false; };
  }, []);

  // Filter-driven insights — the ONLY call that re-runs when filters change.
  const filterKey = JSON.stringify(filters || {});
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const f = filters || {};
    const p = new URLSearchParams();
    if (f.broker_id) p.set('broker_id', f.broker_id);
    if (f.client_id) p.set('client_id', f.client_id);
    if (f.site_id) p.set('site_id', f.site_id);
    if (f.template_id) p.set('template_id', f.template_id);
    if (f.from) p.set('submitted_from', f.from);
    if (f.to) p.set('submitted_to', f.to);
    const qs = p.toString() ? `?${p.toString()}` : '';
    api.get<Insights>(`/analytics/risk-insights${qs}`)
      .then((r) => { if (alive) { setInsights(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const sym = regional.currency_symbol || 'R';
  const fmtMoney = useMemo(() => (n: number): string => {
    const v = Math.round(Number(n) || 0);
    if (Math.abs(v) >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000) return `${sym}${(v / 1_000).toFixed(0)}K`;
    return `${sym}${v.toFixed(0)}`;
  }, [sym]);

  const colorKey = JSON.stringify(colors || {});
  const mergedColors: BandColors = useMemo(() => ({ ...DEFAULT_COLORS, ...(colors || {}) }), [colorKey]);

  // Stable context value — recomputed only when the underlying data (not the
  // parent's keystroke-driven re-renders) actually changes. Keeps memoised
  // tiles from re-rendering on every title/name keystroke in the editor.
  const value: DashData = useMemo(() => ({
    insights, ops, distribution, outstanding, loading,
    colors: mergedColors, filters: filters || {}, fmtMoney, sym,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [insights, ops, distribution, outstanding, loading, mergedColors, filterKey, fmtMoney, sym]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useDashData = (): DashData => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useDashData must be used inside DashboardDataProvider');
  return c;
};

export { formatCurrency };
